import { AnimatePresence, motion } from "framer-motion";
import { isEmpty } from "lodash";
import { classNames } from "primereact/utils";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Location } from "../../../models";
import { SearchInputText } from "../../InputText";

interface TreeNode {
    children?: TreeNode[];
    id: string;
    isActive?: boolean;
    isExpanded?: boolean;
    name: string;
}

interface PanelTemplateProps {
    onLocationSelect?: ({
        companyId,
        locationId,
    }: {
        companyId: string;
        locationId: string;
    }) => void;
    onSearchChange?: (search: string) => void;
    onToggleExpand?: (options: TreeNode[]) => void;
    options?: TreeNode[];
    search?: string;
    selectedLocation?: Location;
}

const PanelTemplate = (props: PanelTemplateProps) => {
    const {
        onLocationSelect = () => {},
        onSearchChange = () => {},
        onToggleExpand = () => {},
        options = [],
        search = "",
        selectedLocation,
    } = props;

    const { t } = useTranslation(["common"]);

    const activeNodeRef = useRef<HTMLDivElement>(null);

    // Scroll active node into view (centered and instantly) on initial mount or tab change
    useEffect(() => {
        if (activeNodeRef.current) {
            activeNodeRef.current.scrollIntoView({
                block: "center",
                behavior: "auto",
            });
        }
    }, []);

    const handleToggleExpand = (company: TreeNode) => {
        onToggleExpand(
            options.map((option) => {
                if (company.id === option.id) {
                    return {
                        ...option,
                        isExpanded: !option.isExpanded,
                    };
                }
                return option;
            })
        );
    };

    return (
        <div>
            <div className="p-2.5 bg-surface-515 border-b border-surface-525">
                <SearchInputText
                    className="w-full"
                    isAnimated={false}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </div>
            <div className="max-h-[50vh] overflow-y-auto">
                {options.map((company) => (
                    <div key={`node-template-${company.id}`}>
                        <div
                            className={
                                "flex gap-1.5 justify-between p-1.5 min-h-10 cursor-pointer hover:bg-surface-515 transition-colors duration-150 rounded-[6px] items-center"
                            }
                            onClick={() => handleToggleExpand(company)}
                        >
                            <div className="flex items-center font-bold">
                                <motion.i
                                    className={classNames(
                                        "w-7 p-1.5 text-surface-700 hover:text-text-500 cursor-pointer transition-colors duration-150",
                                        {
                                            "pi pi-chevron-right": !isEmpty(
                                                company.children
                                            ),
                                        }
                                    )}
                                    animate={{
                                        rotate: company.isExpanded ? 90 : 0,
                                    }}
                                    transition={{
                                        duration: 0.1,
                                        ease: "easeInOut",
                                    }}
                                    initial={false}
                                />

                                {company.name}
                            </div>
                        </div>
                        <AnimatePresence initial={false}>
                            {company.isExpanded && (
                                <motion.div
                                    className="ml-3.5 overflow-hidden"
                                    initial={{
                                        height: 0,
                                        opacity: 0,
                                    }}
                                    animate={{
                                        height: "auto",
                                        opacity: 1,
                                    }}
                                    exit={{
                                        height: 0,
                                        opacity: 0,
                                    }}
                                    transition={{
                                        duration: 0.2,
                                        ease: "easeInOut",
                                    }}
                                >
                                    {company.children?.map((location) => (
                                        <div
                                            key={location.id}
                                            ref={
                                                selectedLocation?.id ===
                                                location.id
                                                    ? activeNodeRef
                                                    : null
                                            }
                                            className={classNames(
                                                "flex gap-1.5 justify-between p-1.5 pl-9 min-h-10 cursor-pointer hover:bg-surface-515 transition-colors duration-150 rounded-[6px]  items-center",
                                                {
                                                    "bg-primary-500/15!":
                                                        selectedLocation?.id ===
                                                        location.id,
                                                }
                                            )}
                                            onClick={() =>
                                                onLocationSelect({
                                                    companyId: company.id,
                                                    locationId: location.id,
                                                })
                                            }
                                        >
                                            {location.name}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
                {options.length === 0 && (
                    <div className="flex justify-center items-center h-10">
                        {t("common:noResultsFound")}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PanelTemplate;
